import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Badge, Col, Row } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

import config from '../../../../config.js';

import { getLoggedUser, getUserPreference } from '../../UserActions.js';
import { getUserGroup, getUserGroupOption, getIsUserGroupFromParent, USER_GROUP_DEFAULT } from '../../UserGroupActions.js';
import { getOrdersByUser, getOrdersByUserRequest } from '../../../Order/OrderActions.js';
import { isPopinActive, setPopinActive } from '../../../App/AppActions.js';
import { AFFILIATION_QUERY_PARAM } from '../../../Affiliation/AffiliationActions.js';
import { getProducts, getProductsRequest, getProductsFromStore } from '../../../Product/ProductActions.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';

import { getFile } from '../../../../util/file.js';
import Icon from '../../../../components/Content/Icon.js';
import SocialShare from '../../../../components/Social/SocialShare.js';
import classNames from 'classnames';

// import './ParrainagePopin.css';

const DISMISS_KEY = 'parrainage-popin-dismissed';
const POINTS = config.fidelity?.pointsPerInvitation || 100;

function ParrainagePopin({ user, orders, userGroup, isUserGroupFromParent, popinActive, adaptative, fidelityGifts, productsLoaded, locale, dispatch, intl }) {
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [showGifts, setShowGifts] = useState(false);

    useEffect(() => {
        if(typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY)) {
            setDismissed(true);
        }
    }, []);

    useEffect(() => {
        if(user && !orders.length) {
            dispatch(getOrdersByUserRequest());
        }
    }, [user]);

    const isIndividual = userGroup?.identifier === USER_GROUP_DEFAULT || isUserGroupFromParent;
    const isEligible = user && orders.length > 0 && isIndividual && getUserGroupOption(userGroup, 'enableFidelity');

    if(!isEligible || dismissed) return null;

    const handleOpen = () => {
        if(!popinActive) {
            setOpen(true);
            dispatch(setPopinActive(true));
        }
    };

    const handleClose = () => {
        setOpen(false);
        dispatch(setPopinActive(false));
    };

    const handleDismiss = () => {
        if(typeof window !== 'undefined') {
            sessionStorage.setItem(DISMISS_KEY, '1');
        }
        setDismissed(true);
        if(open) {
            setOpen(false);
            dispatch(setPopinActive(false));
        }
    };

    const handleToggleGifts = () => {
        if(!showGifts && !productsLoaded) {
            dispatch(getProductsRequest());
        }
        setShowGifts(v => !v);
    };

    const affiliationUrl = `${config.url}${getUserPreference(user, 'language')}/?${AFFILIATION_QUERY_PARAM}=${user._id}&utm_campaign=fidelity-affiliation&utm_medium=invitation&utm_source={network}`;

    return (
        <Fragment>
            <Button variant="warning" size="sm" className="pill w-100 py-1 my-0 parrainage-pill" onClick={handleOpen}>
                🎁 <span className={classNames(adaptative && ['d-none', 'd-md-inline'])}><FormattedMessage id="parrainagePillLabel" defaultMessage="Refer a friend" /></span>
            </Button>

            <Modal size="lg" show={open} onHide={handleClose} centered contentClassName="bg-info bg-texture">
                <Modal.Body className="p-4">

                    <div className="text-center pb-3 border-bottom mb-3">
                        <h2 className="h4 fw-semibold text-primary mb-2">
                            <FormattedMessage id="parrainageModalTitle" defaultMessage="Invite a friend, earn rewards" />
                        </h2>
                        <p className="text-muted small mb-0 mx-auto parrainage-modal__intro">
                            <FormattedMessage
                                id="parrainageModalIntro"
                                defaultMessage="For every friend who places their first order via your link, you earn <b>{points} loyalty points</b> to redeem for a gift."
                                values={{
                                    points: POINTS,
                                    b: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </p>
                    </div>

                    <div className="parrainage-modal__steps">
                        <div className="parrainage-modal__step">
                            <span className="parrainage-modal__step-emoji" aria-hidden="true">📤</span>
                            <strong className="d-block small fw-bold text-primary mb-1">
                                <FormattedMessage id="parrainageStep1Title" defaultMessage="Share" />
                            </strong>
                            <span className="text-muted parrainage-modal__step-text">
                                <FormattedMessage id="parrainageStep1" defaultMessage="Send your personal link to friends" />
                            </span>
                        </div>

                        <span className="parrainage-modal__step-arrow" aria-hidden="true">→</span>

                        <div className="parrainage-modal__step">
                            <span className="parrainage-modal__step-emoji" aria-hidden="true">🛒</span>
                            <strong className="d-block small fw-bold text-primary mb-1">
                                <FormattedMessage id="parrainageStep2Title" defaultMessage="They order" />
                            </strong>
                            <span className="text-muted parrainage-modal__step-text">
                                <FormattedMessage id="parrainageStep2" defaultMessage="Your referral places their first order" />
                            </span>
                        </div>

                        <span className="parrainage-modal__step-arrow" aria-hidden="true">→</span>

                        <div className="parrainage-modal__step parrainage-modal__step--reward">
                            <span className="parrainage-modal__step-emoji" aria-hidden="true">🎁</span>
                            <strong className="d-block parrainage-modal__step-reward-pts mb-1">
                                +{POINTS} pts
                            </strong>
                            <span className="text-muted parrainage-modal__step-text">
                                <FormattedMessage id="parrainageStep3" defaultMessage="to redeem for a gift" />
                            </span>
                        </div>
                    </div>

                    <div className="text-center mb-3">
                        <Button variant="link" size="sm" className="text-primary fw-semibold" onClick={handleToggleGifts}>
                            ✨ <FormattedMessage id="parrainageGiftsToggle" defaultMessage="See available gifts" />
                            {' '}{showGifts ? '▲' : '▼'}
                        </Button>
                    </div>

                    {showGifts && (
                        <div className="d-flex flex-wrap gap-2 mb-3 p-3 rounded bg-white bg-opacity-50">
                            {
								fidelityGifts.length === 0
								? (
									<p className="w-100 text-muted small text-center mb-0">
										<FormattedMessage id="parrainageGiftsEmpty" defaultMessage="No gifts available at the moment." />
									</p>
								) : (
									<Row className="gx-2 gy-3">
										{fidelityGifts.map(product => {
											const cover = (product.pictures || [])[0];
											const name = getTranslation(product, 'name', locale);
											return (
												<Col xs="4" key={product._id} className="position-relative">
													{
														cover
														? (
															<div className="overflow-hidden rounded-5" style={{ height: '120px' }}>
																<img src={getFile(cover)} alt={name} className="w-100 parrainage-modal__gift-img" loading="lazy" />
															</div>
														) : <div className="parrainage-modal__gift-img bg-light" style={{ height: '120px' }} aria-hidden="true" />
													}
													<div className="d-flex flex-column align-items-center gap-1 mt-1">
														<span className="parrainage-modal__gift-name">{name}</span>
														<div className="position-absolute w-100 text-center " style={{ top: '5px', left: '0' }}>
															<Badge bg="secondary" pill className="bg-secondary-gradient"><span className="h6">{product.fidelityPoints} pts</span></Badge>
														</div>
													</div>
												</Col>
											);
										})}
									</Row>
								)
							}
                        </div>
                    )}

                    <div className="border-top pt-3 text-center">
                        <p className="parrainage-modal__share-label text-uppercase fw-semibold text-primary small mb-3">
                            <FormattedMessage id="parrainageShareLabel" defaultMessage="Share my link" />
                        </p>
                        <SocialShare
                            type="parrainage-fidelity"
                            id={user._id}
                            url={affiliationUrl}
                            title={intl.formatMessage(
                                { id: 'fidelityAffiliationInvitationTitle', defaultMessage: 'I invite you to check the {app} shop' },
                                { app: config.application.name },
                            )}
                            text={intl.formatMessage(
                                { id: 'fidelityAffiliationInvitationText', defaultMessage: 'Hey! I discovered this shop {app} and I think you will love it!' },
                                { app: config.application.name },
                            )}
                        />
                    </div>

                    <div className="text-center mt-3">
                        <Button variant="light" onClick={handleClose} aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}>
                            <Icon icon="close" /> <FormattedMessage id="close" defaultMessage="Close" />
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
}

function mapStateToProps(store) {
    const user = getLoggedUser(store);
    return {
        user,
        orders: user ? getOrdersByUser(store, user._id) : [],
        userGroup: getUserGroup(store),
        isUserGroupFromParent: getIsUserGroupFromParent(store),
        popinActive: isPopinActive(store),
        productsLoaded: getProductsFromStore(store).length > 0,
        fidelityGifts: getProducts(store, { isActive: true, isAvailable: true })
            .filter(p => p.fidelityPoints > 0)
            .sort((a, b) => a.fidelityPoints - b.fidelityPoints),
        locale: store.intl.locale,
    };
}

ParrainagePopin.defaultProps = {
    user: null,
    userGroup: null,
    isUserGroupFromParent: false,
    orders: [],
    popinActive: false,
    fidelityGifts: [],
    productsLoaded: false,
    locale: 'fr',
};

ParrainagePopin.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    user: PropTypes.object,
    orders: PropTypes.arrayOf(PropTypes.shape({})),
    fidelityGifts: PropTypes.arrayOf(PropTypes.shape({})),
    productsLoaded: PropTypes.bool,
    locale: PropTypes.string,
    userGroup: PropTypes.object,
    isUserGroupFromParent: PropTypes.bool,
    popinActive: PropTypes.bool,
    adaptative: PropTypes.bool,
};

export default connect(mapStateToProps)(injectIntl(ParrainagePopin));
